/**
 * The selection bar's bulk writes (`equipment-page-bulk-actions`, R3–R4, R6–R7, R9): *Rename as a
 * Set*, *Set 3D Model* and *Keep Off Plan*. *Change Product* goes through the identify call instead
 * (`EquipmentChangeProductPopover`), which brings its own chunking, signed Undo and toasts.
 *
 * Every write is the existing `PATCH twin-assets/:id` through `updateTwinAsset`, bounded by the
 * transport's own timeout (`MIDDLEWARE_REQUEST_TIMEOUT_MS`, 30 s a write) and never retried here: an
 * unknown outcome is settled by the register re-read, and *Try again* is the person's. There is no
 * deadline across a run — an operator started it, it scales with the selection, and every row is
 * accounted for when it ends. Each write queues behind the row's own earlier writes
 * (`queueRowWrite`), so a cell edit and a bulk write of one row never cross.
 *
 * - **Rename** writes one row at a time, in `orderRenames`' order, and stops at the first failure:
 *   the order is what keeps every step clear of a name another row still holds, so carrying on past
 *   a failure would ask the server for names that are not yet free. A row left on a temporary name
 *   is put back before anything is said.
 * - **3D model** and **Keep off plan** write four at a time and do not stop: each row is independent.
 * - **Undo** reads the register just before each write and leaves a row someone has changed since.
 * - The register is re-read once a run ends, and the toast (with its Undo) shows at once. Until that
 *   read lands the register still shows each row as it was, so a check before a write also accepts
 *   the value a row had before this action: sending a value a row already holds changes nothing.
 * - An **unknown outcome** (timeout, dropped connection, gateway) is settled from that re-read: a
 *   row the read shows written counts as written, and its Undo covers it.
 *
 * One run at a time (R9): `busy` names it, and a second press while it runs is refused. Logs carry
 * ids, counts and statuses, never names.
 */
import React, { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { Icon, useToast } from '@repo/design-system';
import { useEquipmentCatalogStore } from '@repo/front-end-state';
import {
  isWriteOutcomeUnknown,
  updateTwinAsset,
  type TwinAssetResponse,
} from '@repo/front-end-services';
import {
  classifyKeepOffPlan,
  nameKey,
  orderRenames,
  registerRowName,
  type AssetPlanState,
  type RenameMove,
  type SetRenamePlan,
} from '@repo/front-end-utils';

import {
  NOT_SURE,
  queueRowWrite,
  reasonOf,
  statusOf,
  type EquipmentWriteDeps,
  type RowWriteFields,
} from './useEquipmentRowWrites';

export type BulkKind = 'rename' | 'model' | 'keep';

/** Writes in flight for *Set 3D Model* and *Keep Off Plan* (spec R6, R7; NFR1). */
export const BULK_WRITE_CONCURRENCY = 4;

/** Failed rows named in one log line; the count is always whole. */
const LOGGED_FAILURES = 20;

/** How long an unknown outcome waits for the re-read to say what landed. */
const REREAD_WAIT_MS = 5000;

/** The server's refusal of a mark on a row with no product (`assetWrite.ts`, `KEEP_OFF_PLAN_UNIDENTIFIED`). */
export const KEEP_OFF_UNIDENTIFIED = /can be kept off the plan/i;

const storeRow = (id: number): TwinAssetResponse | undefined =>
  useEquipmentCatalogStore.getState().registerRows.find(row => row.id === id);

/** The row's name as the table shows it (`registerRowName`), or `undefined` for a row gone. */
const nameOf = (row: TwinAssetResponse | undefined): string | undefined =>
  row ? registerRowName(row) : undefined;

/**
 * Settles an unknown outcome from the re-read the caller has just issued (`reload`, called in the
 * same tick): `holds(row)` once that read has landed, or false when it failed, was never issued or
 * has not landed within `REREAD_WAIT_MS`. The cached row is never taken as confirmation: until the
 * read lands it may still show the value from before the write (Copilot review of #2358).
 */
const afterReread = (id: number, holds: (row: TwinAssetResponse | undefined) => boolean) =>
  new Promise<boolean>(resolve => {
    if (!useEquipmentCatalogStore.getState().isLoading) {
      resolve(false);
      return;
    }
    let unsubscribe = () => undefined as void;
    const timer = window.setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, REREAD_WAIT_MS);
    unsubscribe = useEquipmentCatalogStore.subscribe(state => {
      if (state.isLoading) return;
      window.clearTimeout(timer);
      unsubscribe();
      resolve(!state.error && holds(storeRow(id)));
    });
  });

interface WriteItem {
  id: number;
  fields: RowWriteFields;
  /** Read at the row's turn, against the register as it is then; false sends nothing. */
  precondition?: () => boolean;
  /** The row as the register shows it once this write has landed: settles an unknown outcome. */
  landed?: (row: TwinAssetResponse | undefined) => boolean;
}

interface RunResult {
  written: number[];
  failed: { id: number; error: unknown }[];
  /** The precondition no longer held at the row's turn: nothing was sent. */
  skipped: number[];
  /** Rows never reached (a twin switch). */
  unsent: number[];
  /** Another twin is on screen now: nothing is said, nothing re-read. */
  stale: boolean;
}

const icon = (name: string) => <Icon name={name as React.ComponentProps<typeof Icon>['name']} />;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/*
 * The bar's one-write-at-a-time lock (R9), per twin, at module scope. The bar unmounts when its
 * selection empties or the step changes, while its runs and their toasts carry on; a lock held by
 * the component would be forgotten by the bar mounted next, and a new action could overlap writes
 * still in flight (Copilot review of #2358). Change Product's identify calls are counted here too.
 */
type TwinKey = number | null;
/** One claimed run: released by the twin it was claimed for, whatever twin is on screen then. */
type Claim = { twin: TwinKey };
const bulkRuns = new Map<TwinKey, BulkKind>();
const identifyRuns = new Map<TwinKey, number>();
const lockListeners = new Set<() => void>();
const notifyLock = () => lockListeners.forEach(listener => listener());
const subscribeLock = (listener: () => void) => {
  lockListeners.add(listener);
  return () => {
    lockListeners.delete(listener);
  };
};

/** Change Product's call or Undo has started saving for `twin`. */
export const identifyStarted = (twin: TwinKey) => {
  identifyRuns.set(twin, (identifyRuns.get(twin) ?? 0) + 1);
  notifyLock();
};
/** …and has finished. */
export const identifyEnded = (twin: TwinKey) => {
  const left = (identifyRuns.get(twin) ?? 1) - 1;
  if (left > 0) identifyRuns.set(twin, left);
  else identifyRuns.delete(twin);
  notifyLock();
};
export const identifyRunning = (twin: TwinKey) => identifyRuns.has(twin);
/** Re-renders while Change Product saves for `twin`, from whichever bar started it. */
export const useIdentifyRunning = (twin: TwinKey) =>
  useSyncExternalStore(subscribeLock, () => identifyRuns.has(twin));

/** Said when a write is pressed while another of the bar's writes saves (R9). */
export const ANOTHER_CHANGE_SAVING = 'Another change is still saving — try again when it finishes';

export const useEquipmentBulkWrites = (
  deps: EquipmentWriteDeps,
  /** The bar's other writer, *Change Product*'s identify call and its Undo: true while it saves. */
  othersBusy?: () => boolean,
  /**
   * The page's clear of a plan model, for a row with no product whose model this hook cleared: the
   * plan's mirror never writes a null over a plan model (Rob, 2026-10-06; the asset card's own
   * clear does the same).
   */
  onModelCleared?: (rowId: number) => void,
) => {
  const toast = useToast();
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const othersBusyRef = useRef(othersBusy);
  othersBusyRef.current = othersBusy;
  const onModelClearedRef = useRef(onModelCleared);
  onModelClearedRef.current = onModelCleared;
  const modelCleared = (ids: readonly number[]) =>
    ids.forEach(id => onModelClearedRef.current?.(id));
  const busy = useSyncExternalStore(subscribeLock, () => bulkRuns.get(deps.twinId) ?? null);
  /** True while one of the bar's runs saves for this twin; read by its identify writes (R9). */
  const isBusy = useCallback(() => bulkRuns.has(depsRef.current.twinId), []);

  /*
   * True from a run's end until the re-read it issued lands (at most `REREAD_WAIT_MS`). The bar
   * holds its actions meanwhile, so the next one reads the rows as written, not as they were
   * (Copilot review of #2358). A toast's Undo does not wait: its checks accept a row before or
   * after the write.
   */
  const [settling, setSettling] = useState(false);
  const rereadsPending = useRef(0);
  const holdUntilReread = () => {
    if (!useEquipmentCatalogStore.getState().isLoading) return;
    rereadsPending.current += 1;
    setSettling(true);
    let unsubscribe = () => undefined as void;
    let timer = 0;
    const landed = () => {
      window.clearTimeout(timer);
      unsubscribe();
      rereadsPending.current -= 1;
      if (rereadsPending.current === 0) setSettling(false);
    };
    timer = window.setTimeout(landed, REREAD_WAIT_MS);
    unsubscribe = useEquipmentCatalogStore.subscribe(state => {
      if (!state.isLoading) landed();
    });
  };

  /** Claims the bar for one run; null when another is running for this twin, here or Change Product (R9). */
  const claim = (kind: BulkKind): Claim | null => {
    const twin = depsRef.current.twinId;
    if (bulkRuns.has(twin) || othersBusyRef.current?.()) return null;
    bulkRuns.set(twin, kind);
    notifyLock();
    return { twin };
  };
  const release = (held: Claim) => {
    bulkRuns.delete(held.twin);
    notifyLock();
  };
  /** A toast's Undo or *Try again* pressed while another run saves: said, and offered again. */
  const refusedWhileBusy = (label: string, again: () => void) =>
    toast.show({
      tone: 'neutral',
      icon: icon('circle-info'),
      title: ANOTHER_CHANGE_SAVING,
      actions: [{ label, onAction: again }],
    });

  /**
   * One run of PATCHes through the step's save state: one `begin`, one `settle`, and the register
   * re-read once at the end (never per row). Rows go `concurrency` at a time. A row whose outcome
   * is unknown is counted from that re-read (`landed`), never sent again.
   */
  const runWrites = useCallback(
    async (
      items: readonly WriteItem[],
      { concurrency }: { concurrency: number },
    ): Promise<RunResult> => {
      const issuedFor = depsRef.current.twinId;
      const result: RunResult = { written: [], failed: [], skipped: [], unsent: [], stale: false };
      if (items.length === 0) return result;
      const saveToken = depsRef.current.begin();
      const queue = [...items];
      const worker = async () => {
        while (queue.length > 0) {
          if (depsRef.current.currentTwinId() !== issuedFor) {
            result.unsent.push(...queue.splice(0).map(item => item.id));
            return;
          }
          const item = queue.shift() as WriteItem;
          const outcome = await queueRowWrite(item.id, async () => {
            const d = depsRef.current;
            if (d.currentTwinId() !== issuedFor) return 'unsent' as const;
            if (item.precondition && !item.precondition()) return 'skipped' as const;
            try {
              const token = await d.getToken();
              if (d.currentTwinId() !== issuedFor) return 'unsent' as const;
              await updateTwinAsset(d.baseUrl, token, item.id, item.fields);
              return 'written' as const;
            } catch (error) {
              return { error };
            }
          });
          if (outcome === 'written') result.written.push(item.id);
          else if (outcome === 'skipped') result.skipped.push(item.id);
          else if (outcome === 'unsent') result.unsent.push(item.id);
          else result.failed.push({ id: item.id, error: outcome.error });
        }
      };
      await Promise.all(
        Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, worker),
      );
      const d = depsRef.current;
      result.stale = d.currentTwinId() !== issuedFor;
      const byId = new Map(items.map(item => [item.id, item]));
      const unknown = result.stale
        ? []
        : result.failed.filter(f => byId.get(f.id)?.landed && isWriteOutcomeUnknown(f.error));
      // Forgotten before the re-read, as the rename path does: the store merges a row's local write
      // over the server's answer, so a row added this session would come back with its old values.
      const touched = [...result.written, ...unknown.map(f => f.id)];
      if (touched.length > 0) d.forgetWrites(touched);
      if (!result.stale) {
        d.reload();
        holdUntilReread();
      }
      // An unknown outcome is settled by the re-read just issued, not by a second send.
      if (unknown.length > 0) {
        const settled = await Promise.all(
          unknown.map(f => afterReread(f.id, byId.get(f.id)!.landed!)),
        );
        const landed = new Set(unknown.filter((_, i) => settled[i]).map(f => f.id));
        result.failed = result.failed.filter(f => !landed.has(f.id));
        result.written.push(...Array.from(landed));
      }
      const first = result.failed[0];
      d.settle(
        saveToken,
        first
          ? { ok: false, reason: reasonOf(first.error, 'The change was not saved.') }
          : { ok: true },
      );
      return result;
    },
    [],
  );

  const logFailures = (kind: BulkKind, result: RunResult, total: number) => {
    if (result.failed.length === 0 || result.stale) return;
    console.warn('[equipment] bulk write failed', {
      twinId: depsRef.current.twinId,
      kind,
      rows: total,
      failed: result.failed.length,
      // Every failed row (capped), each with its status, so the toast's count can be traced.
      failures: result.failed
        .slice(0, LOGGED_FAILURES)
        .map(f => ({ assetId: f.id, status: statusOf(f.error) ?? null })),
    });
  };

  // ---------------------------------------------------------------------------------------------
  // Rename as a Set (R3, R4)

  /**
   * Moves rows from their names to new ones, one write at a time in a safe order. Each write first
   * checks the register still shows the name the row is moving from. On a failure it stops, puts
   * any row parked on a temporary name back, and reports which row failed and why.
   */
  const applyRenames = useCallback(
    async (
      moves: readonly RenameMove[],
    ): Promise<{
      done: RenameMove[];
      failure: { id: number; name: string; reason: string } | null;
      leftOnTemporary: { id: number; from: string; name: string }[];
      stale: boolean;
    }> => {
      const current = new Map(moves.map(move => [move.id, move.from]));
      const byId = new Map(moves.map(move => [move.id, move]));
      // A temporary name is never one another register row holds: the server would refuse it.
      const outside = new Set(
        useEquipmentCatalogStore
          .getState()
          .registerRows.filter(row => !byId.has(row.id))
          .map(row => nameKey(registerRowName(row))),
      );
      const steps = orderRenames(moves, key => outside.has(key));
      // The register is not re-read between steps, so a row this run has already moved still shows
      // the name it started from; one not re-read since the action this run undoes may still show
      // the name it is going back to. Any other name means someone changed the row since.
      const items: WriteItem[] = steps.map(step => ({
        id: step.id,
        fields: { asset_name: step.to },
        precondition: () => {
          const shown = nameOf(storeRow(step.id));
          const move = byId.get(step.id) as RenameMove;
          return shown === current.get(step.id) || shown === move.from || shown === move.to;
        },
      }));
      // `runWrites` writes rows in order one at a time; `current` follows each landed step so the
      // next step's precondition reads the name it should now find. Run step by step for that.
      const issuedFor = depsRef.current.twinId;
      let failure: { id: number; name: string; reason: string } | null = null;
      let stale = false;
      const saveToken = depsRef.current.begin();
      const written = new Set<number>();
      for (let i = 0; i < items.length; i++) {
        const d = depsRef.current;
        if (d.currentTwinId() !== issuedFor) {
          stale = true;
          break;
        }
        const step = steps[i];
        const item = items[i];
        // The row as the operator knows it, never a temporary name.
        const from = (byId.get(step.id) as RenameMove).from;
        const outcome = await queueRowWrite(step.id, async () => {
          if (item.precondition && !item.precondition()) return { refused: 'changed' as const };
          try {
            const token = await depsRef.current.getToken();
            if (depsRef.current.currentTwinId() !== issuedFor) return 'unsent' as const;
            await updateTwinAsset(depsRef.current.baseUrl, token, step.id, item.fields);
            return 'written' as const;
          } catch (error) {
            return { error };
          }
        });
        if (outcome === 'written') {
          current.set(step.id, step.to);
          written.add(step.id);
          continue;
        }
        if (outcome === 'unsent') {
          stale = true;
          break;
        }
        if ('refused' in outcome) {
          failure = {
            id: step.id,
            name: from,
            reason: 'it was changed since — the register has been read again',
          };
          break;
        }
        // An unknown outcome is settled by the re-read below, not by a second send.
        if (isWriteOutcomeUnknown(outcome.error)) {
          depsRef.current.forgetWrites([step.id]);
          depsRef.current.reload();
          const landed = await afterReread(step.id, row => nameOf(row) === step.to);
          if (landed) {
            current.set(step.id, step.to);
            written.add(step.id);
            continue;
          }
          failure = { id: step.id, name: from, reason: NOT_SURE };
        } else {
          failure = {
            id: step.id,
            name: from,
            reason: reasonOf(outcome.error, 'The change was not saved.'),
          };
        }
        console.warn('[equipment] bulk write failed', {
          twinId: issuedFor,
          kind: 'rename',
          assetId: step.id,
          step: i,
          steps: steps.length,
          status: 'error' in outcome ? (statusOf(outcome.error) ?? null) : null,
        });
        break;
      }

      // A row parked on a temporary name goes back to where it started before anything is said.
      const leftOnTemporary: { id: number; from: string; name: string }[] = [];
      if (stale) {
        // Another twin is on screen: nothing is written for this one now, but a row parked on a
        // temporary name is never dropped without a trace.
        for (const [id, name] of Array.from(current.entries())) {
          const move = byId.get(id) as RenameMove;
          if (name !== move.from && name !== move.to) {
            console.error('[equipment] temporary name not restored', {
              twinId: issuedFor,
              assetId: id,
              status: null,
            });
          }
        }
      }
      if (failure && !stale) {
        // `Array.from`, never `for…of` over the Map: this app compiles to ES5 without
        // downlevelIteration, where that iterates nothing (`equipmentRowEdit.ts`).
        for (const [id, name] of Array.from(current.entries())) {
          const move = byId.get(id) as RenameMove;
          const parked = name !== move.from && name !== move.to;
          if (!parked) continue;
          try {
            const token = await depsRef.current.getToken();
            // At its turn, as every step: a row the register now shows under another name was
            // renamed from the table while this run saved, and is left as it was renamed.
            const restored = await queueRowWrite(id, async () => {
              const shown = nameOf(storeRow(id));
              if (shown !== move.from && shown !== move.to && shown !== name) return false;
              await updateTwinAsset(depsRef.current.baseUrl, token, id, { asset_name: move.from });
              return true;
            });
            if (!restored) {
              console.warn('[equipment] parked row renamed since, not put back', {
                twinId: issuedFor,
                assetId: id,
              });
              continue;
            }
            current.set(id, move.from);
          } catch (error) {
            leftOnTemporary.push({ id, from: move.from, name });
            console.error('[equipment] temporary name not restored', {
              twinId: issuedFor,
              assetId: id,
              status: statusOf(error) ?? null,
            });
          }
        }
      }

      const d = depsRef.current;
      if (written.size > 0) d.forgetWrites(Array.from(written));
      d.settle(saveToken, failure ? { ok: false, reason: failure.reason } : { ok: true });
      if (!stale && d.currentTwinId() === issuedFor) {
        d.reload();
        holdUntilReread();
      }
      const done = moves.filter(move => current.get(move.id) === move.to && move.from !== move.to);
      return { done, failure, leftOnTemporary, stale };
    },
    [],
  );

  const undoRename = useCallback(
    async (given: readonly RenameMove[]) => {
      const held = claim('rename');
      if (!held) {
        refusedWhileBusy('Undo', () => void undoRename(given));
        return;
      }
      try {
        // Only rows still holding the name this action gave them (or their old one, until the
        // re-read after the rename lands); the rest were changed since.
        const still = given.filter(move => {
          const shown = nameOf(storeRow(move.id));
          return shown === move.to || shown === move.from;
        });
        const changedSince = given.length - still.length;
        if (still.length === 0) {
          toast.show({
            tone: 'neutral',
            icon: icon('circle-info'),
            title: `Nothing to undo — all ${given.length} were changed since`,
          });
          return;
        }
        const back = still.map(move => ({ id: move.id, from: move.to, to: move.from }));
        const outcome = await applyRenames(back);
        if (outcome.stale) return;
        const since =
          changedSince > 0
            ? ` · ${changedSince} ${plural(changedSince, 'was', 'were')} changed since, so ${plural(changedSince, 'was', 'were')} left`
            : '';
        if (outcome.failure) {
          const undone = new Set(outcome.done.map(move => move.id));
          const rest = still.filter(move => !undone.has(move.id));
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Undid ${outcome.done.length} of ${back.length} — ${outcome.failure.name} couldn’t be renamed back: ${outcome.failure.reason}${since}`,
            actions: [
              {
                label: 'Try again',
                onAction: () => void undoRename(rest),
              },
            ],
          });
          return;
        }
        toast.show({
          tone: 'success',
          icon: icon('check'),
          title: `Undid ${outcome.done.length}${since}`,
        });
      } finally {
        release(held);
      }
    },
    // `claim`/`release` read refs; `toast` and `applyRenames` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, applyRenames],
  );

  /**
   * *Rename as a Set*: `plan` from `planSetRename` over the selection in shown order. Resolves
   * whether anything was written, for the popover to close on.
   */
  const renameSet = useCallback(
    async (rows: readonly { id: number; name: string }[], plan: SetRenamePlan) => {
      if (plan.empty) return 'refused' as const;
      const held = claim('rename');
      if (!held) return 'busy' as const;
      try {
        const moves = rows
          .map(row => ({ id: row.id, from: row.name, to: plan.targets.get(row.id) as string }))
          .filter(move => move.from !== move.to);
        if (moves.length === 0) {
          toast.show({
            tone: 'neutral',
            icon: icon('circle-info'),
            title: 'Nothing to rename — these already have those names',
          });
          return 'nothing' as const;
        }
        const outcome = await applyRenames(moves);
        if (outcome.stale) return 'stale' as const;
        const done = outcome.done;
        const undo =
          done.length > 0 ? { label: 'Undo', onAction: () => void undoRename(done) } : undefined;
        if (outcome.failure) {
          const left =
            outcome.leftOnTemporary.length > 0
              ? ` ${outcome.leftOnTemporary
                  .map(row => `${row.from} is left as ${row.name} — rename it in the table.`)
                  .join(' ')}`
              : '';
          // The row that failed in the title, the server's reason on the second line: a toast is
          // one 320 px line, and the reason cut it off (found in the real-app check).
          const title = `Renamed ${done.length} of ${moves.length} — ${outcome.failure.name} couldn’t be saved`;
          const why = `${outcome.failure.reason}${left}`;
          toast.show(
            undo
              ? {
                  tone: 'error',
                  icon: icon('circle-exclamation'),
                  title,
                  sub: why,
                  inlineAction: undo,
                }
              : { tone: 'error', icon: icon('circle-exclamation'), title, detail: why },
          );
          return 'failed' as const;
        }
        toast.show({
          tone: 'success',
          icon: icon('check'),
          title: `Renamed ${done.length}`,
          inlineAction: undo as { label: string; onAction: () => void },
        });
        return 'done' as const;
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, applyRenames, undoRename],
  );

  // ---------------------------------------------------------------------------------------------
  // Set 3D Model (R6)

  /** `choice` is a model file, or `null` for *Use Product's*. */
  const setModel = useCallback(
    async (rows: readonly TwinAssetResponse[], choice: string | null): Promise<boolean> => {
      const held = claim('model');
      if (!held) return false;
      try {
        const targets = rows.filter(row =>
          choice === null ? row.model_3d_source === 'override' : row.model_3d !== choice,
        );
        const already = rows.length - targets.length;
        if (targets.length === 0) {
          toast.show({
            tone: 'neutral',
            icon: icon('circle-info'),
            title:
              choice === null
                ? `Nothing to change — all ${rows.length} already use the product’s model`
                : `Nothing to change — all ${rows.length} already have this model`,
          });
          return true;
        }
        const previous = new Map(
          targets.map(row => [
            row.id,
            {
              model: row.model_3d ?? null,
              override: row.model_3d_source === 'override',
              product: row.reference_id ?? null,
            },
          ]),
        );
        const result = await runWrites(
          targets.map(row => ({
            id: row.id,
            fields: { model_3d: choice },
            precondition: () => (storeRow(row.id)?.model_3d ?? null) === (row.model_3d ?? null),
            landed: (now: TwinAssetResponse | undefined) =>
              !!now &&
              (choice === null ? now.model_3d_source !== 'override' : now.model_3d === choice),
          })),
          { concurrency: BULK_WRITE_CONCURRENCY },
        );
        if (result.stale) return true;
        if (choice === null) {
          modelCleared(result.written.filter(id => previous.get(id)?.product === null));
        }
        logFailures('model', result, targets.length);
        const k = result.written.length;
        const failedCount = result.failed.length + result.skipped.length;
        const undo = () =>
          void undoModel(
            result.written.map(id => ({ id, wrote: choice, previous: previous.get(id)! })),
          );
        const sameAlready =
          choice === null && already > 0
            ? `${already} already ${plural(already, 'uses', 'use')} the product’s`
            : null;
        if (k === 0) {
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Couldn’t set the 3D model: ${reasonOf(result.failed[0]?.error, 'they were changed since')}`,
            actions: [{ label: 'Try again', onAction: () => void setModel(rows, choice) }],
          });
          return false;
        }
        const title =
          failedCount > 0
            ? `3D model set for ${k} of ${targets.length} — ${failedCount} couldn’t be saved`
            : `3D model set for ${k}`;
        toast.show({
          tone: failedCount > 0 ? 'warning' : 'success',
          icon: icon(failedCount > 0 ? 'triangle-exclamation' : 'check'),
          title,
          ...(sameAlready ? { sub: sameAlready } : {}),
          inlineAction: { label: 'Undo', onAction: undo },
        });
        return true;
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, runWrites],
  );

  const undoModel = useCallback(
    async (
      written: readonly {
        id: number;
        wrote: string | null;
        previous: { model: string | null; override: boolean; product: string | null };
      }[],
    ) => {
      const held = claim('model');
      if (!held) {
        refusedWhileBusy('Undo', () => void undoModel(written));
        return;
      }
      try {
        // Still what this action wrote (the file, or — for *Use Product's* — the product's model),
        // or what it had before, until the re-read after the change lands. Checked again just
        // before each row's write: a table edit made meanwhile is left alone (R6, R9). A row on
        // another product is changed since too: *Use Product's* leaves no override, and neither does
        // a product change, so the model alone cannot tell them apart.
        type Entry = (typeof written)[number];
        const holdsWrote = (entry: Entry, row: TwinAssetResponse) =>
          entry.wrote === null ? row.model_3d_source !== 'override' : row.model_3d === entry.wrote;
        const holdsPrevious = (entry: Entry, row: TwinAssetResponse) =>
          entry.previous.override
            ? row.model_3d_source === 'override' && row.model_3d === entry.previous.model
            : row.model_3d_source !== 'override';
        const unchanged = (entry: Entry) => {
          const row = storeRow(entry.id);
          return (
            !!row &&
            (row.reference_id ?? null) === entry.previous.product &&
            (holdsWrote(entry, row) || holdsPrevious(entry, row))
          );
        };
        const still = written.filter(unchanged);
        const changedSince = written.length - still.length;
        if (still.length === 0) {
          toast.show({
            tone: 'neutral',
            icon: icon('circle-info'),
            title: `Nothing to undo — all ${written.length} were changed since`,
          });
          return;
        }
        const result = await runWrites(
          still.map(entry => ({
            id: entry.id,
            fields: { model_3d: entry.previous.override ? entry.previous.model : null },
            precondition: () => unchanged(entry),
            landed: (row: TwinAssetResponse | undefined) => !!row && holdsPrevious(entry, row),
          })),
          { concurrency: BULK_WRITE_CONCURRENCY },
        );
        if (result.stale) return;
        const clearedIds = new Set(
          still
            .filter(entry => !entry.previous.override && entry.previous.product === null)
            .map(entry => entry.id),
        );
        modelCleared(result.written.filter(id => clearedIds.has(id)));
        logFailures('model', result, still.length);
        const left = changedSince + result.skipped.length;
        const since = left > 0 ? ` · ${left} changed since, so left` : '';
        if (result.failed.length > 0) {
          const failedIds = new Set(result.failed.map(f => f.id));
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Undid ${result.written.length} of ${still.length}${since}`,
            actions: [
              {
                label: 'Try again',
                onAction: () => void undoModel(still.filter(entry => failedIds.has(entry.id))),
              },
            ],
          });
          return;
        }
        toast.show({
          tone: 'success',
          icon: icon('check'),
          title: `3D model change undone for ${result.written.length}${since}`,
        });
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, runWrites],
  );

  // ---------------------------------------------------------------------------------------------
  // Keep Off Plan (R7)

  const keepOffPlan = useCallback(
    async (
      rows: readonly TwinAssetResponse[],
      planStateOf: (id: number) => AssetPlanState | undefined,
    ): Promise<boolean> => {
      const held = claim('keep');
      if (!held) return false;
      try {
        const buckets = classifyKeepOffPlan(rows, planStateOf);
        if (buckets.eligible.length === 0) return false;
        const result = await runWrites(
          buckets.eligible.map(id => ({
            id,
            fields: { keep_off_plan: true },
            precondition: () => storeRow(id)?.keep_off_plan !== true,
            landed: (row: TwinAssetResponse | undefined) => row?.keep_off_plan === true,
          })),
          { concurrency: BULK_WRITE_CONCURRENCY },
        );
        if (result.stale) return true;
        // A row that lost its product meanwhile is refused by the server's identified-only rule:
        // it is counted with the rows that have no product, not as a failure.
        const lostProduct = result.failed.filter(
          f => statusOf(f.error) === 400 && KEEP_OFF_UNIDENTIFIED.test(reasonOf(f.error, '')),
        );
        const failed = result.failed.filter(f => !lostProduct.includes(f));
        logFailures('keep', { ...result, failed }, buckets.eligible.length);
        const k = result.written.length;
        const clauses = (
          [
            [buckets.onPlan.length, 'is on the plan', 'are on the plan'],
            [
              buckets.noProduct.length + lostProduct.length,
              'has no product yet',
              'have no product yet',
            ],
            [buckets.notTracked.length, 'is Not tracked', 'are Not tracked'],
            [
              buckets.keptOff.length + result.skipped.length,
              'was already kept off',
              'were already kept off',
            ],
            [failed.length, 'couldn’t be saved', 'couldn’t be saved'],
          ] as [number, string, string][]
        )
          .filter(([n]) => n > 0)
          .map(([n, one, many]) => `${n} ${plural(n, one, many)}`);
        if (k === 0) {
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Couldn’t keep these off the plan${clauses.length ? ` · ${clauses.join(' · ')}` : ''}`,
            actions:
              failed.length > 0
                ? [
                    {
                      label: 'Try again',
                      onAction: () => void keepOffPlan(rows, planStateOf),
                    },
                  ]
                : [],
          });
          return false;
        }
        toast.show({
          tone: failed.length > 0 ? 'warning' : 'success',
          icon: icon(failed.length > 0 ? 'triangle-exclamation' : 'eye-slash'),
          title: `${k} kept off the plan`,
          ...(clauses.length > 0 ? { sub: clauses.join(' · ') } : {}),
          inlineAction: { label: 'Undo', onAction: () => void undoKeepOff(result.written) },
        });
        return true;
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, runWrites],
  );

  /**
   * Takes the mark off a list of rows (R5): the one write loop behind *Put Back On Plan* and the
   * Keep Off Plan toast's Undo. Claims nothing — each caller holds the `keep` lock and says its own
   * toast. Marked, or not marked yet as far as a register not re-read since knows: taking off a
   * mark a row does not hold changes nothing. Only a row gone is left.
   */
  const takeMarkOff = useCallback(
    (ids: readonly number[]) =>
      runWrites(
        ids.map(id => ({
          id,
          fields: { keep_off_plan: false },
          precondition: () => storeRow(id) !== undefined,
          landed: (row: TwinAssetResponse | undefined) => !!row && row.keep_off_plan !== true,
        })),
        { concurrency: BULK_WRITE_CONCURRENCY },
      ),
    [runWrites],
  );

  const undoKeepOff = useCallback(
    async (ids: readonly number[]) => {
      const held = claim('keep');
      if (!held) {
        refusedWhileBusy('Undo', () => void undoKeepOff(ids));
        return;
      }
      try {
        const result = await takeMarkOff(ids);
        if (result.stale) return;
        logFailures('keep', result, ids.length);
        const since =
          result.skipped.length > 0 ? ` · ${result.skipped.length} changed since, so left` : '';
        if (result.failed.length > 0) {
          const failedIds = result.failed.map(f => f.id);
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Undid ${result.written.length} of ${ids.length}${since}`,
            actions: [{ label: 'Try again', onAction: () => void undoKeepOff(failedIds) }],
          });
          return;
        }
        toast.show({
          tone: 'success',
          icon: icon('check'),
          title:
            result.written.length > 0
              ? `${result.written.length} back to Not on plan${since}`
              : `Nothing to undo — all ${ids.length} were changed since`,
        });
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, takeMarkOff],
  );

  // ---------------------------------------------------------------------------------------------
  // Put Back On Plan (`equipment-page-keep-off-plan-undo`, R4–R7)

  /**
   * Re-marks rows a put back took the mark off (R7). A row gone, or placed since (read from the plan
   * as it is now: `planStateOf` is a live read, the toast outlives the render), is left; one that
   * lost its product is refused by the server's identified-only rule and said so. The mark itself is
   * not checked: an Undo pressed before the put back's re-read lands reads the row still marked, and
   * marking a marked row changes nothing (as `undoKeepOff` reads it).
   */
  const undoPutBack = useCallback(
    async (
      ids: readonly number[],
      planStateOf: (id: number) => AssetPlanState | undefined,
    ): Promise<void> => {
      const held = claim('keep');
      if (!held) {
        refusedWhileBusy('Undo', () => void undoPutBack(ids, planStateOf));
        return;
      }
      try {
        const result = await runWrites(
          ids.map(id => ({
            id,
            fields: { keep_off_plan: true },
            precondition: () => {
              const row = storeRow(id);
              return !!row && planStateOf(id) !== 'on_plan';
            },
            landed: (row: TwinAssetResponse | undefined) => row?.keep_off_plan === true,
          })),
          { concurrency: BULK_WRITE_CONCURRENCY },
        );
        if (result.stale) return;
        const lostProduct = result.failed.filter(
          f => statusOf(f.error) === 400 && KEEP_OFF_UNIDENTIFIED.test(reasonOf(f.error, '')),
        );
        const failed = result.failed.filter(f => !lostProduct.includes(f));
        logFailures('keep', { ...result, failed }, ids.length);
        const k = result.written.length;
        const clauses = (
          [
            [result.skipped.length, 'changed since, so left', 'changed since, so left'],
            [lostProduct.length, 'lost its product, so left', 'lost their product, so left'],
          ] as [number, string, string][]
        )
          .filter(([n]) => n > 0)
          .map(([n, one, many]) => ` · ${n} ${plural(n, one, many)}`)
          .join('');
        if (failed.length > 0) {
          const failedIds = failed.map(f => f.id);
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Kept ${k} of ${ids.length} off the plan again${clauses}`,
            actions: [
              { label: 'Try again', onAction: () => void undoPutBack(failedIds, planStateOf) },
            ],
          });
          return;
        }
        toast.show({
          tone: 'success',
          icon: icon(k > 0 ? 'eye-slash' : 'check'),
          title:
            k > 0
              ? `${k} kept off the plan again${clauses}`
              : lostProduct.length === 0
                ? `Nothing to undo — all ${ids.length} were changed since`
                : `Nothing to undo${clauses}`,
        });
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, runWrites],
  );

  /** Takes the mark off the selection's kept-off rows (R4), and says what came of it (R6). */
  const putBackOnPlan = useCallback(
    async (
      rows: readonly TwinAssetResponse[],
      planStateOf: (id: number) => AssetPlanState | undefined,
      fromToast = false,
    ): Promise<boolean> => {
      const held = claim('keep');
      if (!held) {
        // The bar's own press is aria-disabled while busy; a toast's Try again is told why.
        if (fromToast)
          refusedWhileBusy('Try again', () => void putBackOnPlan(rows, planStateOf, true));
        return false;
      }
      try {
        const targets = classifyKeepOffPlan(rows, planStateOf).keptOff;
        if (targets.length === 0) return false;
        const result = await takeMarkOff(targets);
        if (result.stale) return true;
        logFailures('keep', result, targets.length);
        const k = result.written.length;
        const clauses = (
          [
            [result.failed.length, 'couldn’t be saved', 'couldn’t be saved'],
            [result.skipped.length, 'changed since, so left', 'changed since, so left'],
          ] as [number, string, string][]
        )
          .filter(([n]) => n > 0)
          .map(([n, one, many]) => `${n} ${plural(n, one, many)}`);
        if (k === 0) {
          toast.show({
            tone: 'error',
            icon: icon('circle-exclamation'),
            title: `Couldn’t put these back on the plan${clauses.length ? ` · ${clauses.join(' · ')}` : ''}`,
            actions:
              result.failed.length > 0
                ? [
                    {
                      label: 'Try again',
                      onAction: () => void putBackOnPlan(rows, planStateOf, true),
                    },
                  ]
                : [],
          });
          return false;
        }
        const offers = `Floor Plan offers ${k === 1 ? 'it' : 'them'} again.`;
        const partial = result.failed.length > 0 || result.skipped.length > 0;
        toast.show({
          tone: partial ? 'warning' : 'success',
          icon: icon(partial ? 'triangle-exclamation' : 'eye'),
          title: `${k} put back on the plan.`,
          sub: [offers, ...clauses].join(' · '),
          inlineAction: {
            label: 'Undo',
            onAction: () => void undoPutBack(result.written, planStateOf),
          },
        });
        return true;
      } finally {
        release(held);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, takeMarkOff, undoPutBack],
  );

  return { busy, settling, isBusy, renameSet, setModel, keepOffPlan, putBackOnPlan };
};
